// The procedural map generator.
//
// A level is grown as a self-avoiding walk of axis-aligned rectangular tiles on a
// 100-stud grid — exactly how the source mapexample.obj is built (8 Roblox parts laid
// edge to edge). A moving "cursor" runs forward emitting tiles; it turns 90 degrees
// through a junction, widens into a room, or ramps up/down a level. Occupancy is tracked
// per grid cell so the path never crosses itself.
//
// Placement uses a deterministic backtracking DFS: at each node an rng-weighted, ordered
// list of candidate moves is tried first-fit; a dead end undoes the last move and tries
// the next candidate. This reliably reaches the target length while keeping the winding,
// varied character of the hand-built example. Dead-end side rooms are added afterward.

import {
  CELL,
  CORRIDOR_HW,
  ROOM_HALF_WIDTHS,
  STRAIGHT_CELLS,
  ROOM_CELLS,
  RAMP_CELLS,
  BRANCH_CELLS,
  ARENA_HALF_WIDTHS,
  ARENA_CELLS,
  BRIDGE_CELLS,
  CHAMBER_HW,
  CHAMBER_ROOM_CELLS,
  LANE_SLOT_CELLS,
  LANE_SEP_CELLS,
  LANE_SPAN_CELLS,
  LANE_PAD_CELLS,
  MIN_PLATFORM_HALF_CELLS,
  MIN_PLATFORM_LEN_CELLS,
  levelPlan,
  type LevelPlan,
} from "../config";
import { Rng, levelSeed } from "../engine/rng";

/** Cardinal direction: 0=+Z (north), 1=+X (east), 2=-Z (south), 3=-X (west). */
export type Dir = 0 | 1 | 2 | 3;
export const DVEC: readonly (readonly [number, number])[] = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
];

export type TileKind =
  | "start"
  | "corridor"
  | "room"
  | "corner"
  | "ramp"
  | "branch"
  | "exit"
  | "arena" // open battle plaza
  | "core" // the finale enemy-core chamber
  | "bridge" // elevated catwalk (thin floating deck)
  | "chamber"; // split-level chamber floor (low/high halves + full-width ramp)

export interface RampInfo {
  travelDir: Dir;
  backLevel: number;
  frontLevel: number;
}

export interface Tile {
  id: number;
  kind: TileKind;
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  level: number; // top-surface elevation level (for a ramp, the higher end)
  ramp?: RampInfo;
}

export interface CellInfo {
  level: number;
  kind: TileKind;
  /** For ramp cells: axis of travel (0 = along Z, 1 = along X). A level change is only
   *  walkable across a ramp edge that is parallel to this axis (the ends), not its sides. */
  rampAxis?: 0 | 1;
}

export interface LevelStats {
  tiles: number;
  corridors: number;
  rooms: number;
  corners: number;
  ramps: number;
  branches: number;
  turns: number;
  arenas: number;
  bridges: number;
  chambers: number;
  pathLength: number;
  minElev: number;
  maxElev: number;
  bbox: { minX: number; maxX: number; minZ: number; maxZ: number };
}

/** One lane split: K parallel sub-routes between a shared split pad and merge pad. Each lane
 *  is a list of "gate" cell keys (ordered split->merge) that a unit is force-routed through so
 *  the army spreads evenly across the parallel routes. Diamonds are listed in spine order. */
export interface LaneDiamond {
  lanes: string[][]; // per lane: gate cell keys, ordered from the split side to the merge side.
}

export interface GeneratedLevel {
  level: number;
  seedWord: string;
  seedInt: number;
  tiles: Tile[];
  cells: Map<string, CellInfo>;
  start: { x: number; z: number; level: number };
  exit: { x: number; z: number; level: number };
  lanes: LaneDiamond[];
  stats: LevelStats;
  plan: LevelPlan;
  reachedTarget: boolean;
}

interface Cursor {
  x: number;
  z: number;
  dir: Dir;
  level: number;
}

interface Rect {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

interface Snap {
  tileCount: number;
  cellKeys: string[];
  laneCount: number;
  x: number;
  z: number;
  dir: Dir;
  level: number;
  pathLength: number;
}

type Cand = () => boolean;

const cellKey = (cx: number, cz: number): string => `${cx},${cz}`;
const lensDesc = ([min, max]: [number, number]): number[] => {
  const a: number[] = [];
  for (let l = max; l >= min; l--) a.push(l);
  return a;
};

/** Rectangle grown forward from a cursor: `length` along heading, `hw` half-width either side. */
function forwardRect(cur: Cursor, length: number, hw: number): Rect {
  const [dx, dz] = DVEC[cur.dir];
  const [px, pz] = DVEC[(cur.dir + 1) & 3];
  const xs: number[] = [];
  const zs: number[] = [];
  for (const s of [0, length]) {
    for (const t of [-hw, hw]) {
      xs.push(cur.x + dx * s + px * t);
      zs.push(cur.z + dz * s + pz * t);
    }
  }
  return {
    x0: Math.min(...xs),
    x1: Math.max(...xs),
    z0: Math.min(...zs),
    z1: Math.max(...zs),
  };
}

const CORE_MIN_RETRIES = 10; // re-roll budget to get the core up to the shared minimum footprint

/** Public entry. Generates the level, re-rolling with a perturbed RNG until the enemy CORE reaches
 *  the shared minimum base footprint (start & core both ≥ MIN_PLATFORM); keeps the largest-core
 *  attempt if none hit it (an extremely cramped seed). Deterministic per (seed, level). */
export function generateLevel(seedWord: string, level: number): GeneratedLevel {
  const minShort = MIN_PLATFORM_LEN_CELLS; // 6
  const minLong = MIN_PLATFORM_HALF_CELLS * 2; // 8
  const coreCells = (lvl: GeneratedLevel): [number, number] => {
    const c = lvl.tiles.find((t) => t.kind === "core");
    if (!c) return [0, 0];
    return [Math.round((c.x1 - c.x0) / CELL), Math.round((c.z1 - c.z0) / CELL)];
  };
  const meetsMin = (lvl: GeneratedLevel): boolean => {
    const [w, d] = coreCells(lvl);
    return Math.min(w, d) >= minShort && Math.max(w, d) >= minLong;
  };
  let best = generateOnce(seedWord, level, 0);
  if (meetsMin(best)) return best;
  let bestArea = coreCells(best)[0] * coreCells(best)[1];
  for (let a = 1; a <= CORE_MIN_RETRIES; a++) {
    const cand = generateOnce(seedWord, level, a);
    if (meetsMin(cand)) return cand;
    const [w, d] = coreCells(cand);
    if (w * d > bestArea) {
      best = cand;
      bestArea = w * d;
    }
  }
  return best;
}

/** One generation pass. `attempt` perturbs the RNG so the wrapper can re-roll for a bigger core
 *  (attempt 0 = the level's canonical seed, unchanged). */
function generateOnce(seedWord: string, level: number, attempt: number): GeneratedLevel {
  const plan = levelPlan(level);
  const seedInt = levelSeed(seedWord, level);
  const rng = new Rng(attempt === 0 ? seedInt : (seedInt ^ (attempt * 0x9e3779b1)) >>> 0);
  const B = plan.boundHalf;

  const tiles: Tile[] = [];
  const cells = new Map<string, CellInfo>();
  const moveKeys: string[] = []; // cell keys touched by the in-flight move (for undo)
  let nextId = 0;
  let pathLength = 0;
  const cur: Cursor = { x: 0, z: 0, dir: 0, level: 0 };

  // ---- low-level helpers ----
  const cellsOf = (r: Rect): string[] => {
    const out: string[] = [];
    const cx0 = Math.round(r.x0 / CELL);
    const cx1 = Math.round(r.x1 / CELL);
    const cz0 = Math.round(r.z0 / CELL);
    const cz1 = Math.round(r.z1 / CELL);
    for (let cx = cx0; cx < cx1; cx++)
      for (let cz = cz0; cz < cz1; cz++) out.push(cellKey(cx, cz));
    return out;
  };
  const free = (keys: string[]): boolean => {
    for (const k of keys) if (cells.has(k)) return false;
    return true;
  };
  const inBounds = (r: Rect): boolean =>
    r.x0 >= -B && r.x1 <= B && r.z0 >= -B && r.z1 <= B;

  const commit = (r: Rect, kind: TileKind, level_: number, storeLevel: number, ramp?: RampInfo): void => {
    tiles.push({ id: nextId++, kind, ...r, level: level_, ramp });
    const info: CellInfo = { level: storeLevel, kind };
    if (ramp) info.rampAxis = (ramp.travelDir & 1) === 0 ? 0 : 1;
    for (const k of cellsOf(r)) {
      cells.set(k, info);
      moveKeys.push(k);
    }
  };

  const advance = (dist: number): void => {
    const [dx, dz] = DVEC[cur.dir];
    cur.x += dx * dist;
    cur.z += dz * dist;
    pathLength += dist;
  };

  const openness = (fromX: number, fromZ: number, dir: Dir): number => {
    const [dx, dz] = DVEC[dir];
    let cx = Math.round(fromX / CELL);
    let cz = Math.round(fromZ / CELL);
    let n = 0;
    for (let i = 0; i < 6; i++) {
      cx += dx;
      cz += dz;
      if (cells.has(cellKey(cx, cz))) break;
      if (Math.abs(cx * CELL) > B || Math.abs(cz * CELL) > B) break;
      n++;
    }
    return n;
  };

  // ---- deterministic placement actions ----
  const tryStraightExact = (lenCells: number): boolean => {
    const r = forwardRect(cur, lenCells * CELL, CORRIDOR_HW);
    if (!inBounds(r) || !free(cellsOf(r))) return false;
    commit(r, "corridor", cur.level, cur.level);
    advance(lenCells * CELL);
    return true;
  };
  const tryRoomExact = (hw: number, lenCells: number): boolean => {
    const r = forwardRect(cur, lenCells * CELL, hw);
    if (!inBounds(r) || !free(cellsOf(r))) return false;
    commit(r, "room", cur.level, cur.level);
    advance(lenCells * CELL);
    return true;
  };
  const tryCornerExact = (side: 1 | -1): boolean => {
    const sq = forwardRect(cur, 2 * CORRIDOR_HW, CORRIDOR_HW);
    if (!inBounds(sq) || !free(cellsOf(sq))) return false;
    const ndir = ((((cur.dir + side) % 4) + 4) % 4) as Dir;
    const [dx, dz] = DVEC[cur.dir];
    const scx = cur.x + dx * CORRIDOR_HW;
    const scz = cur.z + dz * CORRIDOR_HW;
    const [ndx, ndz] = DVEC[ndir];
    const nx = scx + ndx * CORRIDOR_HW;
    const nz = scz + ndz * CORRIDOR_HW;
    if (openness(nx, nz, ndir) < 2) return false; // need room to exit the turn
    commit(sq, "corner", cur.level, cur.level);
    cur.x = nx;
    cur.z = nz;
    cur.dir = ndir;
    return true;
  };
  const tryRampExact = (ascend: boolean): boolean => {
    if (ascend && cur.level >= plan.maxElev) return false;
    if (!ascend && cur.level <= 0) return false;
    const back = cur.level;
    const front = ascend ? cur.level + 1 : cur.level - 1;
    const r = forwardRect(cur, RAMP_CELLS * CELL, CORRIDOR_HW);
    if (!inBounds(r) || !free(cellsOf(r))) return false;
    commit(r, "ramp", Math.max(back, front), front, {
      travelDir: cur.dir,
      backLevel: back,
      frontLevel: front,
    });
    advance(RAMP_CELLS * CELL);
    cur.level = front;
    return true;
  };

  // ---- combat segments ----
  const tryArenaExact = (hw: number, lenCells: number): boolean => {
    const r = forwardRect(cur, lenCells * CELL, hw);
    if (!inBounds(r) || !free(cellsOf(r))) return false;
    commit(r, "arena", cur.level, cur.level);
    advance(lenCells * CELL);
    return true;
  };
  const tryBridgeExact = (lenCells: number): boolean => {
    if (cur.level < 1) return false; // a catwalk wants a drop beneath it
    const r = forwardRect(cur, lenCells * CELL, CORRIDOR_HW);
    if (!inBounds(r) || !free(cellsOf(r))) return false;
    commit(r, "bridge", cur.level, cur.level);
    advance(lenCells * CELL);
    return true;
  };
  // Split-level chamber: low half (N) -> full-width ramp -> high half (N+1). Atomic: all three
  // sub-rects must fit before any is committed, so backtracking stays consistent.
  const tryChamberExact = (hw: number, frontLen: number, backLen: number): boolean => {
    if (cur.level >= plan.maxElev) return false;
    const back = cur.level;
    const front = cur.level + 1;
    const [dx, dz] = DVEC[cur.dir];
    const step = (n: number, lvl: number): Cursor => ({
      x: cur.x + dx * n,
      z: cur.z + dz * n,
      dir: cur.dir,
      level: lvl,
    });
    const frontR = forwardRect(cur, frontLen * CELL, hw);
    const rampR = forwardRect(step(frontLen * CELL, back), RAMP_CELLS * CELL, hw);
    const backR = forwardRect(step((frontLen + RAMP_CELLS) * CELL, front), backLen * CELL, hw);
    if (!inBounds(frontR) || !inBounds(rampR) || !inBounds(backR)) return false;
    if (!free([...cellsOf(frontR), ...cellsOf(rampR), ...cellsOf(backR)])) return false;
    commit(frontR, "chamber", back, back);
    commit(rampR, "ramp", front, front, { travelDir: cur.dir, backLevel: back, frontLevel: front });
    commit(backR, "chamber", front, front);
    advance((frontLen + RAMP_CELLS + backLen) * CELL);
    cur.level = front;
    return true;
  };

  // ---- lanes: the path opens into K parallel sub-routes (a diamond) that rejoin ----
  const laneDiamonds: LaneDiamond[] = [];

  // Axis-aligned world rect from cursor-frame coords: `a` along the heading, `l` lateral (perp).
  // Valid for any cardinal heading (DVEC components are 0/±1); edges stay on the CELL grid as
  // long as a/l are whole CELL multiples.
  const frameRect = (a0: number, a1: number, l0: number, l1: number): Rect => {
    const [dx, dz] = DVEC[cur.dir];
    const [px, pz] = DVEC[(cur.dir + 1) & 3];
    const xs: number[] = [];
    const zs: number[] = [];
    for (const a of [a0, a1]) {
      for (const l of [l0, l1]) {
        xs.push(cur.x + dx * a + px * l);
        zs.push(cur.z + dz * a + pz * l);
      }
    }
    return { x0: Math.min(...xs), x1: Math.max(...xs), z0: Math.min(...zs), z1: Math.max(...zs) };
  };
  // Signed distance of a cell center along the current heading (orders lane gates split->merge).
  const alongOf = (k: string): number => {
    const [cx, cz] = k.split(",").map(Number);
    const [dx, dz] = DVEC[cur.dir];
    return ((cx + 0.5) * CELL - cur.x) * dx + ((cz + 0.5) * CELL - cur.z) * dz;
  };

  // Atomic lane diamond: split pad -> K parallel flat sub-routes of differing shape -> merge pad.
  // Every rect must fit before any is committed (backtracking stays consistent). All cells sit at
  // cur.level so the seams are walkable (stepBetween true) and only the empty inter-lane gaps rail.
  const tryLanesExact = (K: number, span: number, styles: number[]): boolean => {
    const HW = CORRIDOR_HW; // lane corridor half-width (1 cell)
    const slot = LANE_SLOT_CELLS * CELL;
    const sep = LANE_SEP_CELLS * CELL;
    const pad = LANE_PAD_CELLS * CELL;
    const half = slot / 2;
    const bundleHW = ((K * LANE_SLOT_CELLS + (K - 1) * LANE_SEP_CELLS) * CELL) / 2;
    const slotCenter = (k: number): number => -bundleHW + half + k * (slot + sep);
    const A0 = pad; // lanes begin just past the split pad
    const A1 = pad + span * CELL; // ...and end at the merge pad

    const parts: { r: Rect; kind: TileKind }[] = [
      { r: frameRect(0, pad, -bundleHW, bundleHW), kind: "arena" }, // split pad (shared entry)
      { r: frameRect(A1, A1 + pad, -bundleHW, bundleHW), kind: "arena" }, // merge pad (shared exit)
    ];

    const laneParts: { r: Rect; kind: TileKind }[][] = [];
    for (let k = 0; k < K; k++) {
      const c = slotCenter(k);
      const lane: { r: Rect; kind: TileKind }[] = [];
      switch (styles[k] % 4) {
        case 0: // straight corridor down the slot center
          lane.push({ r: frameRect(A0, A1, c - HW, c + HW), kind: "corridor" });
          break;
        case 1: // wide lane filling the whole slot
          lane.push({ r: frameRect(A0, A1, c - half, c + half), kind: "room" });
          break;
        case 2: {
          // corridor with a room bulge in the middle (three disjoint rects)
          const pinch = span >= 6 ? 2 * CELL : CELL;
          const t1 = A0 + pinch;
          const t2 = A1 - pinch;
          lane.push({ r: frameRect(A0, t1, c - HW, c + HW), kind: "corridor" });
          lane.push({ r: frameRect(t1, t2, c - half, c + half), kind: "room" });
          lane.push({ r: frameRect(t2, A1, c - HW, c + HW), kind: "corridor" });
          break;
        }
        default: {
          // jog: corridor hugs one side, kinks across a 1-cell connector, hugs the other side
          const jog = CELL; // 1-cell lateral shift (jog + HW == half, so it fits the slot)
          const kinkAt = A0 + Math.max(2, Math.floor(span / 2)) * CELL;
          lane.push({ r: frameRect(A0, kinkAt, c - jog - HW, c - jog + HW), kind: "corridor" });
          lane.push({ r: frameRect(kinkAt, kinkAt + CELL, c - jog - HW, c + jog + HW), kind: "corridor" });
          lane.push({ r: frameRect(kinkAt + CELL, A1, c + jog - HW, c + jog + HW), kind: "corridor" });
        }
      }
      laneParts.push(lane);
      parts.push(...lane);
    }

    // atomic: every rect in bounds, and the union of cells is free AND internally disjoint.
    for (const p of parts) if (!inBounds(p.r)) return false;
    const seen = new Set<string>();
    const allKeys: string[] = [];
    for (const p of parts) {
      for (const ck of cellsOf(p.r)) {
        if (seen.has(ck)) return false; // rects must not overlap each other
        seen.add(ck);
        allKeys.push(ck);
      }
    }
    if (!free(allKeys)) return false;

    for (const p of parts) commit(p.r, p.kind, cur.level, cur.level);
    // record each lane's gate cells (entry / mid / exit along the heading) for force-routing
    const diamond: LaneDiamond = { lanes: [] };
    for (const lane of laneParts) {
      const keys = new Set<string>();
      for (const p of lane) for (const ck of cellsOf(p.r)) keys.add(ck);
      const sorted = [...keys].sort((a, b) => alongOf(a) - alongOf(b));
      diamond.lanes.push([sorted[0], sorted[(sorted.length / 2) | 0], sorted[sorted.length - 1]]);
    }
    laneDiamonds.push(diamond);
    advance(A1 + pad);
    return true;
  };

  const target = plan.targetTiles;

  // Ordered candidate moves at the current node, rng-weighted for character.
  const buildCandidates = (): Cand[] => {
    const canUp = cur.level < plan.maxElev;
    const canDown = cur.level > 0;
    const canRamp = canUp || canDown;
    const hasRamp = tiles.some((t) => t.kind === "ramp");
    const hasRoom = tiles.some((t) => t.kind === "room");

    const intents: { intent: string; w: number }[] = [
      { intent: "forward", w: 6 },
      { intent: "turn", w: 2 + plan.pTurn * 8 },
      { intent: "ramp", w: canRamp ? 0.6 + plan.pRamp * 8 : 0 },
      { intent: "arena", w: plan.wArena },
      { intent: "bridge", w: cur.level >= 1 ? plan.wBridge : 0 },
      { intent: "chamber", w: canUp ? plan.wChamber : 0 },
      { intent: "lane", w: laneDiamonds.length < plan.laneMaxSplits ? plan.wLane : 0 },
    ];
    // Nudge the signature variance in (like the example): one elevation change, one room.
    if (canRamp && !hasRamp && tiles.length >= Math.floor(target * 0.4)) {
      intents.find((i) => i.intent === "ramp")!.w += 30;
    }

    // weighted order without replacement
    const pool = intents.filter((i) => i.w > 0);
    const order: string[] = [];
    while (pool.length) {
      const idx = rng.weightedIndex(pool.map((p) => p.w));
      order.push(pool[idx].intent);
      pool.splice(idx, 1);
    }

    const list: Cand[] = [];
    const pushStraight = (): void => {
      for (const len of lensDesc(STRAIGHT_CELLS)) list.push(() => tryStraightExact(len));
    };
    const pushRoom = (): void => {
      const hw = rng.pick(ROOM_HALF_WIDTHS);
      for (const len of lensDesc(ROOM_CELLS)) list.push(() => tryRoomExact(hw, len));
    };
    for (const intent of order) {
      if (intent === "forward") {
        const roomFirst =
          rng.chance(plan.pRoom) || (!hasRoom && tiles.length >= Math.floor(target * 0.45));
        if (roomFirst) {
          pushRoom();
          pushStraight();
        } else {
          pushStraight();
          pushRoom();
        }
      } else if (intent === "turn") {
        const sides: (1 | -1)[] = rng.chance(0.5) ? [1, -1] : [-1, 1];
        for (const s of sides) list.push(() => tryCornerExact(s));
      } else if (intent === "ramp") {
        const dirs: boolean[] = [];
        if (canUp) dirs.push(true);
        if (canDown) dirs.push(false);
        if (dirs.length === 2) {
          // Bias upward when low, downward when near the ceiling, so the path climbs
          // overall and actually uses its elevation range instead of oscillating at 0-1.
          const pAscendFirst = 1 - cur.level / (plan.maxElev + 1);
          if (!rng.chance(pAscendFirst)) dirs.reverse();
        }
        for (const up of dirs) list.push(() => tryRampExact(up));
      } else if (intent === "arena") {
        const hw = rng.pick(ARENA_HALF_WIDTHS);
        for (const len of lensDesc(ARENA_CELLS)) list.push(() => tryArenaExact(hw, len));
      } else if (intent === "bridge") {
        for (const len of lensDesc(BRIDGE_CELLS)) list.push(() => tryBridgeExact(len));
      } else if (intent === "chamber") {
        const fl = rng.int(CHAMBER_ROOM_CELLS[0], CHAMBER_ROOM_CELLS[1]);
        const bl = rng.int(CHAMBER_ROOM_CELLS[0], CHAMBER_ROOM_CELLS[1]);
        list.push(() => tryChamberExact(CHAMBER_HW, fl, bl));
      } else if (intent === "lane") {
        const K = rng.int(2, plan.laneCountMax);
        const span = rng.int(LANE_SPAN_CELLS[0], LANE_SPAN_CELLS[1]);
        // distinct styles per lane so every diamond mixes shapes (straight / wide / room / jog)
        const pool = [0, 1, 2, 3];
        for (let i = pool.length - 1; i > 0; i--) {
          const j = rng.int(0, i);
          [pool[i], pool[j]] = [pool[j], pool[i]];
        }
        list.push(() => tryLanesExact(K, span, pool.slice(0, K)));
      }
    }
    return list;
  };

  // ---- large start plaza heading north (the blue team's buildable base) ----
  commit(forwardRect(cur, plan.startLenCells * CELL, plan.startHalfCells * CELL), "start", 0, 0);
  advance(plan.startLenCells * CELL);
  const start = { x: 0, z: 0, level: 0 };

  // ---- backtracking DFS main path ----
  const frames: { cands: Cand[]; ci: number; snap: Snap }[] = [];
  let cands = buildCandidates();
  let ci = 0;
  let steps = 0;
  const stepCap = target * 120;

  const undo = (s: Snap): void => {
    tiles.length = s.tileCount;
    for (const k of s.cellKeys) cells.delete(k);
    laneDiamonds.length = s.laneCount;
    cur.x = s.x;
    cur.z = s.z;
    cur.dir = s.dir;
    cur.level = s.level;
    pathLength = s.pathLength;
  };

  // Once the path has enough tiles, keep going until the enemy core can ALSO get its guaranteed MIN
  // footprint (shared with the start pad) forward of the end. Post-target we only EXTEND — never
  // backtrack below target — so a cramped end degrades to a smaller core instead of collapsing the
  // whole map (the regeneration wrapper below re-rolls those rare cases to hit the floor).
  const coreFitsForward = (): boolean => {
    const r = forwardRect(cur, MIN_PLATFORM_LEN_CELLS * CELL, MIN_PLATFORM_HALF_CELLS * CELL);
    return inBounds(r) && free(cellsOf(r));
  };

  while (steps < stepCap) {
    steps++;
    if (tiles.length >= target && coreFitsForward()) break; // full path + guaranteed core room
    if (ci >= cands.length) {
      if (tiles.length >= target) break; // target met but can't extend to core room here — accept as-is
      const parent = frames.pop();
      if (!parent) break; // exhausted every path from the start — keep what we have
      undo(parent.snap);
      cands = parent.cands;
      ci = parent.ci + 1;
      continue;
    }
    const beforeCount = tiles.length;
    const beforeLaneCount = laneDiamonds.length;
    const bx = cur.x;
    const bz = cur.z;
    const bdir = cur.dir;
    const blevel = cur.level;
    const bpl = pathLength;
    moveKeys.length = 0;
    if (cands[ci]()) {
      frames.push({
        cands,
        ci,
        snap: {
          tileCount: beforeCount,
          cellKeys: moveKeys.slice(),
          laneCount: beforeLaneCount,
          x: bx,
          z: bz,
          dir: bdir,
          level: blevel,
          pathLength: bpl,
        },
      });
      cands = buildCandidates();
      ci = 0;
    } else {
      ci++;
    }
  }
  const reachedTarget = tiles.length >= target;
  // Path end is the last main-path tile — captured BEFORE branches are appended below.
  const mainEndIdx = tiles.length - 1;

  // ---- enemy-core chamber at the path end (the finale objective) ----
  // Prefer a full core chamber forward, shrinking if tight; else re-tag the last real
  // main-path tile as the core. Update the tile AND its cells so the checker sees a 'core'
  // cell and verifies start->core reachability.
  let coreTileIdx = -1;
  {
    // Greedy largest-fitting plaza forward at the path end (the red team's buildable base): from
    // the plan's ceiling down to the shared MIN floor (never smaller — the DFS only ended here
    // because that floor was guaranteed to fit), biggest footprint first, so it grabs as much room
    // as the wound-up end allows instead of jumping straight to a small fallback.
    const coreCands: [number, number][] = [];
    for (let hw = MIN_PLATFORM_HALF_CELLS; hw <= plan.coreHalfCells; hw++)
      for (let len = MIN_PLATFORM_LEN_CELLS; len <= plan.coreLenCells; len++) coreCands.push([hw, len]);
    coreCands.sort((a, b) => b[0] * b[1] - a[0] * a[1]);
    for (const [hw, len] of coreCands) {
      const r = forwardRect(cur, len * CELL, hw * CELL);
      if (inBounds(r) && free(cellsOf(r))) {
        commit(r, "core", cur.level, cur.level);
        advance(len * CELL);
        coreTileIdx = tiles.length - 1;
        break;
      }
    }
    if (coreTileIdx < 0) {
      const retaggable = new Set(["corridor", "room", "corner", "arena", "chamber", "bridge"]);
      for (let i = mainEndIdx; i >= 0; i--) {
        if (retaggable.has(tiles[i].kind)) {
          coreTileIdx = i;
          break;
        }
      }
      if (coreTileIdx >= 0) {
        const t = tiles[coreTileIdx];
        t.kind = "core";
        for (const ck of cellsOf(t)) {
          const ci = cells.get(ck);
          if (ci) ci.kind = "core";
        }
      }
    }

    // Grow the core rectangle into surrounding EMPTY space (all four faces) so the red base fills
    // whatever room the path end actually has — free() only annexes void, so it never overlaps
    // another tile, and the annexed cells are flat 'core' so solvability/traversability hold.
    if (coreTileIdx >= 0) {
      const t = tiles[coreTileIdx];
      // The base was placed via forwardRect (length along cur.dir, width across it). Cap each
      // WORLD axis by its role: the across-heading axis by the plaza half-width (2*coreHalfCells),
      // the along-heading axis by coreLenCells — so the grown base keeps the intended proportions.
      const forwardIsZ = (cur.dir & 1) === 0;
      const capX = forwardIsZ ? 2 * plan.coreHalfCells : plan.coreLenCells;
      const capZ = forwardIsZ ? plan.coreLenCells : 2 * plan.coreHalfCells;
      const annex = (r: Rect): boolean => {
        if (!inBounds(r) || !free(cellsOf(r))) return false;
        for (const ck of cellsOf(r)) cells.set(ck, { level: t.level, kind: "core" });
        return true;
      };
      const wCells = (): number => Math.round((t.x1 - t.x0) / CELL);
      const lCells = (): number => Math.round((t.z1 - t.z0) / CELL);
      let grew = true;
      while (grew) {
        grew = false;
        if (wCells() < capX && annex({ x0: t.x1, x1: t.x1 + CELL, z0: t.z0, z1: t.z1 })) { t.x1 += CELL; grew = true; }
        if (wCells() < capX && annex({ x0: t.x0 - CELL, x1: t.x0, z0: t.z0, z1: t.z1 })) { t.x0 -= CELL; grew = true; }
        if (lCells() < capZ && annex({ x0: t.x0, x1: t.x1, z0: t.z1, z1: t.z1 + CELL })) { t.z1 += CELL; grew = true; }
        if (lCells() < capZ && annex({ x0: t.x0, x1: t.x1, z0: t.z0 - CELL, z1: t.z0 })) { t.z0 -= CELL; grew = true; }
      }
    }
  }

  // ---- dead-end side rooms off the finished main path (placed AFTER the core so a branch can never
  // steal the core's reserved forward room; free() keeps branches off the committed core cells) ----
  const mainPath = tiles.filter((t) => t.kind === "corridor" || t.kind === "room");
  for (const t of mainPath) {
    if (!rng.chance(plan.pBranch)) continue;
    const cxC = Math.round((t.x0 + t.x1) / 2 / CELL) * CELL;
    const czC = Math.round((t.z0 + t.z1) / 2 / CELL) * CELL;
    const side = rng.int(0, 3) as Dir;
    let bx = cxC;
    let bz = czC;
    if (side === 0) bz = t.z1;
    else if (side === 2) bz = t.z0;
    else if (side === 1) bx = t.x1;
    else bx = t.x0;
    const bcur: Cursor = { x: bx, z: bz, dir: side, level: t.level };
    const depth = rng.int(BRANCH_CELLS[0], BRANCH_CELLS[1]);
    const r = forwardRect(bcur, depth * CELL, CORRIDOR_HW);
    if (inBounds(r) && free(cellsOf(r))) commit(r, "branch", t.level, t.level);
  }

  const exit =
    coreTileIdx >= 0
      ? {
          x: (tiles[coreTileIdx].x0 + tiles[coreTileIdx].x1) / 2,
          z: (tiles[coreTileIdx].z0 + tiles[coreTileIdx].z1) / 2,
          level: tiles[coreTileIdx].level,
        }
      : { x: cur.x, z: cur.z, level: cur.level };

  // ---- stats ----
  const bbox = { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity };
  let minElev = Infinity;
  let maxElev = -Infinity;
  const count: Record<string, number> = {};
  for (const t of tiles) {
    bbox.minX = Math.min(bbox.minX, t.x0);
    bbox.maxX = Math.max(bbox.maxX, t.x1);
    bbox.minZ = Math.min(bbox.minZ, t.z0);
    bbox.maxZ = Math.max(bbox.maxZ, t.z1);
    count[t.kind] = (count[t.kind] ?? 0) + 1;
    if (t.ramp) {
      minElev = Math.min(minElev, t.ramp.backLevel, t.ramp.frontLevel);
      maxElev = Math.max(maxElev, t.ramp.backLevel, t.ramp.frontLevel);
    } else {
      minElev = Math.min(minElev, t.level);
      maxElev = Math.max(maxElev, t.level);
    }
  }
  const stats: LevelStats = {
    tiles: tiles.length,
    corridors: count["corridor"] ?? 0,
    rooms: count["room"] ?? 0,
    corners: count["corner"] ?? 0,
    ramps: count["ramp"] ?? 0,
    branches: count["branch"] ?? 0,
    turns: count["corner"] ?? 0,
    arenas: (count["arena"] ?? 0) + (count["core"] ?? 0),
    bridges: count["bridge"] ?? 0,
    chambers: count["chamber"] ?? 0,
    pathLength,
    minElev: Number.isFinite(minElev) ? minElev : 0,
    maxElev: Number.isFinite(maxElev) ? maxElev : 0,
    bbox,
  };

  return {
    level: plan.level,
    seedWord,
    seedInt,
    tiles,
    cells,
    start,
    exit,
    lanes: laneDiamonds,
    stats,
    plan,
    reachedTarget,
  };
}
